import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
    ActionButton,
    Button,
    ButtonGroup,
    Cell,
    Content,
    ContextualHelp,
    Dialog,
    DialogTrigger,
    Divider,
    Flex,
    Heading,
    Text,
    View,
} from '@adobe/react-spectrum';
import { leaderboardFetchFiltered, SORT_MAP } from '../../data/redux/slices/leaderboardSlice';
import { setAppRoute } from '../../data/redux/slices/appSlice';
import '../users/Users.css';
import { getGrades } from '../../data/ScoreDisplayUtils';
import { Helmet } from 'react-helmet';
import { ResultsListing } from '../resultsListing/ResultsListing';
import { resultsColumns } from '../resultsListing/columns/ResultsColumns';
import { resultsCellRenderers } from '../resultsListing/renderers/ResultsCellRenderers';
import { resultsColumnRenderers } from '../resultsListing/renderers/ResultsColumnRenderers';
import User from '@spectrum-icons/workflow/User';
import { getAnalytics, logEvent } from 'firebase/analytics';
import isMobile from 'ismobilejs';
import {
    authorDetailsFetchFiltered,
    fetchAuthorDetailsItems,
    setAuthorDetailsSortBy,
    setAuthorDetailsSortDirection,
    setSelectedListIndex,
} from '../../data/redux/slices/authorDetailsSlice';
import { BreadCrumbs } from '../breadcrumbs/BreadCrumbs';
import { doRefresh } from '../../data/redux/slices/importSlice';
import { batchRefreshAuthor } from '../../data/redux/slices/authorDetailsSlice';
import Refresh from '@spectrum-icons/workflow/Refresh';
import AddCircle from '@spectrum-icons/workflow/AddCircle';
import { SubmitForm } from '../submitform/SubmitForm';
import { GenericLoadingDialog } from '../genericLoadingDialog/GenericLoadingDialog';
import { getDomainPrefix } from '../../data/DynamoConnector';
import { DownloadIcon } from 'react-modal-image/lib/icons';
import { PrintDialog } from '../widgets/printDialog/PrintDialog';
import { BreadCrumbImportButton } from '../breadcrumbs/buttons/BreadCrumbImportButton';
import { BreadCrumbActionBar } from '../breadcrumbs/BreadCrumbActionBar';
import { formatNumber, truncateToOneDecimal } from '../common/cardStats/formatters';
import { capBracketRating, capPowerLevel } from '../../data/displayCaps';
import { fetchUserDetails } from '../../data/redux/slices/userDetailsSlice';
import { useFilterUrlSync } from '../../utils/useFilterUrlSync';
import { isDefaultFilterState } from '../../utils/filterUrl';

const EXAMPLE_USER_ID = 'f498f418-7061-707d-d071-acfa2afd733c';

export function AuthorDetails() {
    const dispatch = useDispatch();
    const auth = useAuth();
    const ownProfileName = useSelector((state) => state?.profile?.metadata?.profileName);
    const linkedProfiles = useSelector((state) => state?.profile?.profiles);
    let listItems = useSelector((state) => state?.authorDetails?.listItems);
    listItems = (listItems || []).filter((item) => {
        return item && item?.id;
    });
    const nextCursor = useSelector((state) => state?.authorDetails?.nextCursor);
    const isFetching = useSelector((state) => state?.authorDetails?.isFetching);
    const loadingState = useSelector((state) => state?.authorDetails?.loadingState);
    const searchFilters = useSelector((state) => state?.authorDetails?.filters);
    const details = useSelector((state) => state?.authorDetails?.details);
    const sortBy = useSelector((state) => state?.authorDetails?.sortBy);
    const sortDirection = useSelector((state) => state.authorDetails.sortDirection);
    const previewIsShowing = useSelector((state) => state.preview.isShowing);
    const mobile = isMobile(window.navigator).phone;
    const isRefreshing = useSelector((state) => state.authorDetails.isRefreshing);
    const isImporting = useSelector((state) => state.import.isImporting);
    const selectedListKey = useSelector((state) => state.authorDetails.selectedListKey);
    const selectedListIndex = useSelector((state) => state.authorDetails.selectedListIndex);

    const handleFiltersChange = (filters) => {
        dispatch(authorDetailsFetchFiltered(filters, sortBy, sortDirection));
    };

    useFilterUrlSync({
        context: 'decks',
        filters: searchFilters,
        sortBy,
        sortDirection,
        onApply: ({ filters, sortBy: urlSortBy, sortDirection: urlSortDirection }) => {
            if (isDefaultFilterState(filters, urlSortBy, urlSortDirection, 'decks')) return;
            dispatch(setAuthorDetailsSortBy(urlSortBy));
            dispatch(setAuthorDetailsSortDirection(urlSortDirection));
            dispatch(authorDetailsFetchFiltered(filters, urlSortBy, urlSortDirection));
        },
    });

    const handleSortChange = (sortIndex) => {
        dispatch(setAuthorDetailsSortBy(SORT_MAP[sortIndex], searchFilters));
        dispatch(fetchAuthorDetailsItems(null, searchFilters, true, SORT_MAP[sortIndex], sortDirection));
    };

    const handleSortDirectionChange = (direction) => {
        dispatch(setAuthorDetailsSortDirection(direction));
        dispatch(fetchAuthorDetailsItems(null, searchFilters, true, sortBy, direction));
    };

    const grades = details ? getGrades(details) : null;

    const handleLoadMore = () => {
        if (!isFetching && nextCursor !== null) {
            dispatch(fetchAuthorDetailsItems(nextCursor, searchFilters, false, sortBy, sortDirection));
        }
    };

    const handleAuthorLinkPress = () => {
        logEvent(getAnalytics(), 'authors_open_author', { url: details?.metadata?.url });
        window.open(details?.metadata?.url, `_blank`);
    };

    const handleIndexSelection = (evn) => {
        dispatch(setSelectedListIndex(evn));
    };

    const { pathname } = useLocation();
    const urlAuthorId = decodeURIComponent(pathname.split('/').pop() || '');
    const idMatches = details?.id && urlAuthorId && details.id === urlAuthorId;
    const helmetName = idMatches ? `Authors - ${details.id}` : `Author Details - Loading...`;
    const helmetCanonical = `https://www.commandersalt.com/authors/id${details?.id}`;
    const helmetDescription = `MtG Commander decks by ${details?.id}`;
    const helmetTitle = `Commandersalt.com - MtG Commander decks by ${details?.id}`;

    const displayProgressBar = isImporting || isRefreshing;
    const progressDialogHeader = isImporting ? 'Importing deck...' : 'Refreshing - DOES NOT REIMPORT';

    return (
        <Flex direction="column">
            <Helmet>
                <title>{helmetName}</title>
                <meta name="creator" content="Ben Rimbey" />
                <link rel="canonical" href={helmetCanonical} />
                <meta name="description" content={helmetDescription} />
                <meta name="twitter:title" content={helmetTitle} />
                <meta property="og:site_name" content="Commandersalt.com" />
                <meta property="og:title" content={helmetTitle} />
                <meta property="og:image" content="https://www.commandersalt.com/chef-kiss-meta-image.png" />
                <meta property="og:description" content={helmetDescription} />
            </Helmet>
            {displayProgressBar && (
                <GenericLoadingDialog eyebrow={isImporting ? 'Import' : 'Refresh'} title={progressDialogHeader} />
            )}
            <BreadCrumbs
                content={
                    <BreadCrumbActionBar maxVisibleMobile={2}>
                        <BreadCrumbImportButton isDisabled={isImporting || isRefreshing} />
                    </BreadCrumbActionBar>
                }
            />
            <div className="heroPanelWrap">
                <div className="heroPanelCard panelGradient">
                    <div className="heroPanelImages">
                        {details?.metadata?.profileImageUrl ? (
                            <img
                                src={details.metadata.profileImageUrl}
                                alt={details?.username || 'avatar'}
                                className="heroPanelImage heroPanelImage--avatar"
                            />
                        ) : (
                            <div
                                className="heroPanelImageFallback heroPanelImage--avatar"
                                role="img"
                                aria-label={details?.username || 'avatar'}
                            >
                                <span className="heroPanelImageFallbackLabel">No avatar</span>
                                <span className="heroPanelImageFallbackName">
                                    {details?.username || details?.id || 'Unknown'}
                                </span>
                            </div>
                        )}
                    </div>
                    <div className="heroPanelInfo">
                        <div className="heroPanelEyebrow">
                            {`${details?.count > 1 ? 'Average scores' : 'Scores'} · ${details?.count || 0} deck${
                                details?.count === 1 ? '' : 's'
                            }`}
                        </div>
                        <div className="heroPanelName">{details?.username || details?.id || 'Loading…'}</div>
                        <div className="heroPanelStatsGrid">
                            <div className="heroPanelStat">
                                <div className="heroPanelStatLabel">Power Level</div>
                                <div className="heroPanelStatValue" style={{ color: grades?.['powerlevel']?.color }}>
                                    {formatNumber(capPowerLevel(details?.powerLevelRating), 1)}
                                </div>
                            </div>
                            <div className="heroPanelStat">
                                <div className="heroPanelStatLabel">Bracket</div>
                                <div className="heroPanelStatValue" style={{ color: grades?.['bracket']?.color }}>
                                    {truncateToOneDecimal(capBracketRating(details?.bracketRating), '—')}
                                </div>
                            </div>
                            <div className="heroPanelStat">
                                <div className="heroPanelStatLabel">Saltiness</div>
                                <div className="heroPanelStatValue" style={{ color: grades?.['salt']?.color }}>
                                    {formatNumber(details?.saltRating, 1)}
                                </div>
                            </div>
                            <div className="heroPanelStat">
                                <div className="heroPanelStatLabel">Synergy</div>
                                <div className="heroPanelStatValue" style={{ color: grades?.['synergy']?.color }}>
                                    {formatNumber(details?.synergyRating, 1)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {(() => {
                // Three cases:
                //   1. Not logged in                        → sign-up pitch
                //   2. Logged in + author is one of mine    → "Go to my profile"
                //   3. Logged in + author is NOT mine       → render nothing
                //
                // "Mine" = any listItem's author.url matches a profileUrl
                // on my linked-accounts record (moxfield / archidekt /
                // tappedout). Normalize with lowercase + trailing-slash
                // strip so stored handles match deck-row author URLs.
                const isAuthed = !!auth?.isAuthenticated;

                const myProfileUrls = new Set();
                Object.keys(linkedProfiles || {}).forEach((provider) => {
                    Object.keys(linkedProfiles[provider] || {}).forEach((handle) => {
                        const url = linkedProfiles[provider][handle]?.profileUrl;
                        if (url) myProfileUrls.add(url.toLowerCase().replace(/\/$/, ''));
                    });
                });
                const authorMatchesMe =
                    isAuthed &&
                    myProfileUrls.size > 0 &&
                    (listItems || []).some((item) => {
                        const url = item?.author?.url;
                        if (!url) return false;
                        return myProfileUrls.has(url.toLowerCase().replace(/\/$/, ''));
                    });

                const myProfileRoute = ownProfileName ? `/user/id/${encodeURIComponent(ownProfileName)}` : null;

                if (isAuthed) {
                    if (!authorMatchesMe || !myProfileRoute) return null;
                    return (
                        <section className="authorSignupPitch" aria-label="Open your linked profile">
                            <div className="authorSignupPitchEyebrow">This one's yours</div>
                            <div className="authorSignupPitchBody">
                                You have this account linked to your CommanderSalt profile. Head over there for your
                                full play-style read — Timmy / Johnny / Spike / Vorthos / Mel axis, signature cards, top
                                combos, commanders, color mix, archetype mix, and aggregate stats rolled up across every
                                deck on this page.
                            </div>
                            <div className="authorSignupPitchActions">
                                <button
                                    type="button"
                                    className="authorSignupPitchBtn authorSignupPitchBtn--primary"
                                    onClick={() => {
                                        logEvent(getAnalytics(), 'authors_my_profile_cta_click');
                                        // Kick off the user-details fetch first — the app-route
                                        // handler only auto-fetches on the initial page load,
                                        // so SPA-navigating from here won't populate state.
                                        dispatch(fetchUserDetails(ownProfileName));
                                        dispatch(setAppRoute({ route: myProfileRoute }));
                                    }}
                                >
                                    Go to my profile →
                                </button>
                            </div>
                        </section>
                    );
                }

                return (
                    <section className="authorSignupPitch" aria-label="Sign up for a play-style profile">
                        <div className="authorSignupPitchEyebrow">Want more than averages?</div>
                        <div className="authorSignupPitchBody">
                            Make an account and link your Moxfield / Archidekt / TappedOut handle to unlock a full
                            play-style profile: a Timmy / Johnny / Spike / Vorthos / Mel read on how you build, your
                            signature cards and top combos, your most-played commanders, color and archetype mix, plus
                            aggregate stats across every deck you've ever imported. This author page only shows the
                            public roll-up.
                        </div>
                        <div className="authorSignupPitchActions">
                            <button
                                type="button"
                                className="authorSignupPitchBtn authorSignupPitchBtn--primary"
                                onClick={() => {
                                    logEvent(getAnalytics(), 'authors_signup_cta_click');
                                    void auth.signinRedirect();
                                }}
                            >
                                Sign up / log in
                            </button>
                            <button
                                type="button"
                                className="authorSignupPitchBtn authorSignupPitchBtn--ghost"
                                onClick={() => {
                                    logEvent(getAnalytics(), 'authors_signup_example_click');
                                    dispatch(fetchUserDetails(EXAMPLE_USER_ID));
                                    dispatch(setAppRoute({ route: `/user/id/${EXAMPLE_USER_ID}` }));
                                }}
                            >
                                See an example profile →
                            </button>
                        </div>
                    </section>
                );
            })()}
            {/*<PrintDialog authorId={details?.id} pageType={'author'} name={details?.name} />*/}
            <Flex marginTop={50} marginBottom={-40} direction={'row'} wrap justifyContent={'space-around'}>
                <Flex direction={'row'}>
                    <span className={'inlineHeader'}>Some decks may be hidden</span>
                    <ContextualHelp variant="help" marginStart={5}>
                        <Content>
                            <Text>
                                Submitted decks that are illegal for commander format are hidden from this view. <p />{' '}
                                This includes:
                            </Text>
                            <ul>
                                <li>Decks that contain banned/illegal cards</li>
                            </ul>
                            <ul>
                                <li>Decks that are not 100 cards (or 101 with companion)</li>
                            </ul>
                        </Content>
                    </ContextualHelp>
                </Flex>
            </Flex>
            <ResultsListing
                includeSortByIngestDate={true}
                sortBy={sortBy}
                sortDirection={sortDirection}
                handleSortChange={handleSortChange}
                handleSortDirectionChange={handleSortDirectionChange}
                disableNavChanges={true}
                allowDeckPreview={true}
                handleSelectionChange={null}
                handleIndexSelection={handleIndexSelection}
                selectedListIndex={selectedListIndex}
                selectedListIndexPrefix={'authorDetails_'}
                selectedListKey={selectedListKey}
                hasMore={nextCursor !== null}
                columns={resultsColumns.LEADERBOARD()}
                columnRenderer={resultsColumnRenderers.LEADERBOARD}
                listItems={listItems}
                cellRenderer={resultsCellRenderers.LEADERBOARD}
                previewIsShowing={previewIsShowing}
                handleLoadMore={handleLoadMore}
                loadingState={loadingState}
                showFilterPanel={true}
                filterContext="decks"
                onFiltersChange={handleFiltersChange}
                filters={searchFilters}
            />
        </Flex>
    );
}
